import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Truck, Eye, EyeOff, ArrowLeft, Globe, ChevronDown } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t, language, setLanguage, supportedLanguages, getCurrentLanguage } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const languageDropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const isAdminLogin = location.pathname === '/admin/login';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(formData.email, formData.password, isAdminLogin);
      toast.success(t('auth_login_success'));
      navigate(isAdminLogin ? '/admin' : '/');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth_login_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4">
      <div className="max-w-md w-full">
        {/* Top Bar with Back Button and Language Selector */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            data-testid="back-to-app-btn"
          >
            <ArrowLeft size={20} />
            {t('auth_back_to_app')}
          </button>
          
          {/* Language Selector */}
          <div className="relative" ref={languageDropdownRef}>
            <button
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
              data-testid="language-selector-btn"
            >
              <Globe size={18} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                {getCurrentLanguage().flag} {getCurrentLanguage().nativeName}
              </span>
              <ChevronDown size={16} className={`text-gray-400 transition-transform ${showLanguageDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showLanguageDropdown && (
              <div 
                className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50"
                data-testid="language-dropdown"
              >
                {supportedLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setShowLanguageDropdown(false);
                      toast.success(`Language changed to ${lang.nativeName}`);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                      language === lang.code ? 'bg-orange-50 text-orange-600' : 'text-gray-700'
                    }`}
                    data-testid={`language-option-${lang.code}`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <div>
                      <div className="font-medium">{lang.nativeName}</div>
                      <div className="text-xs text-gray-400">{lang.name}</div>
                    </div>
                    {language === lang.code && (
                      <span className="ml-auto text-orange-500">✓</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="text-center mb-8">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
            isAdminLogin 
              ? 'bg-gradient-to-br from-purple-600 to-indigo-700'
              : 'bg-gradient-to-br from-orange-500 to-red-600'
          }`}>
            <Truck size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="login-title">
            {isAdminLogin ? t('auth_admin_login') : t('auth_welcome_back')}
          </h1>
          <p className="text-gray-500 mt-2">
            {isAdminLogin ? t('auth_access_dashboard') : t('auth_sign_in_account')}
          </p>
        </div>

        <Card className="p-6 border-0 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">{t('auth_email_address')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@example.com"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">{t('auth_password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className={`w-full py-6 text-white ${
                isAdminLogin
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800'
                  : 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700'
              }`}
            >
              {loading ? t('auth_signing_in') : t('auth_sign_in')}
            </Button>
          </form>
        </Card>

        {!isAdminLogin && (
          <p className="text-center mt-6 text-gray-600">
            {t('auth_no_account')}{' '}
            <Link to="/register" className="text-orange-600 font-medium hover:underline" data-testid="signup-link">
              {t('auth_sign_up')}
            </Link>
          </p>
        )}

        {!isAdminLogin && (
          <p className="text-center mt-3 text-gray-500 text-sm">
            <Link to="/admin/login" className="hover:underline" data-testid="admin-login-link">
              {t('auth_admin_login')}
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
